import { useCallback, useRef, type MutableRefObject } from 'react';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type MutateOptions,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiList, apiRequest, type Page, type RequestOptions } from './client';
import { ApiError } from './problem';
import { uuidv7 } from './uuidv7';
import { spendGrant } from '../auth/stepup';

/**
 * The query layer.
 *
 * A refusal is never retried blindly: `4xx` means the server decided, and repeating the same
 * request would only repeat the decision. Only a transport failure is worth one retry — and a
 * mutation's retry has to be the SAME logical intent, not a new one, which is what `ActionIntent`
 * below exists to make impossible to get wrong.
 */
export function createQueryClient(onSessionLost: () => void): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            // ONLY a genuine loss of session ends the session (P13-f H4). `auth.step_up_required`
            // is also a 401 and is raised BY an authenticated session; treating it as a logout
            // unmounted the shell before the step-up remedy could be drawn.
            if (error.isSessionLost) {
              onSessionLost();
              return false;
            }
            return error.isTransportFailure && failureCount < 1;
          }
          return false;
        },
      },
      mutations: {
        retry: false,
        onError: (error) => {
          if (error instanceof ApiError && error.isSessionLost) onSessionLost();
        },
      },
    },
  });
}

/** A collection read. The key is the path plus its query, so a filter change is a new query. */
export function useApiList<T>(
  path: string | null,
  query?: RequestOptions['query'],
  options?: { refetchInterval?: number },
): UseQueryResult<Page<T>, Error> {
  return useQuery<Page<T>, Error>({
    queryKey: ['list', path, query ?? null],
    queryFn: () => apiList<T>(path as string, query),
    enabled: path !== null,
    ...(options?.refetchInterval ? { refetchInterval: options.refetchInterval } : {}),
  });
}

export interface Resource<T> {
  data: T;
  version: number | null;
  etag: string | null;
}

/**
 * A single-resource read that KEEPS the version.
 *
 * Every PATCH/PUT in the tenancy surface requires `If-Match`, and the version it compares is the
 * one carried in the read's `ETag` (or in the body's own `version` field when the route does not
 * set an ETag). Losing it is how a compare-and-swap becomes a last-writer-wins overwrite.
 */
export function useApiResource<T extends { version?: number }>(
  path: string | null,
  query?: RequestOptions['query'],
): UseQueryResult<Resource<T>, Error> {
  return useQuery<Resource<T>, Error>({
    queryKey: ['resource', path, query ?? null],
    queryFn: async () => {
      const res = await apiRequest<T>(path as string, query ? { query } : {});
      return {
        data: res.data,
        version: res.version ?? res.data?.version ?? null,
        etag: res.etag,
      };
    },
    enabled: path !== null,
  });
}

/**
 * ONE LOGICAL INTENT, WITH ONE IDEMPOTENCY KEY.
 *
 * `Idempotency-Key` is what makes a mutation safe to repeat: the server stores the outcome under
 * the key and replays it. That only works if the key belongs to the INTENT — "activate release X at
 * version 7" — rather than to the HTTP attempt. A key minted per attempt turns the ambiguous case
 * the header exists for (the response was lost, the write may have committed) into a second,
 * different operation.
 *
 * So an intent is minted once, frozen, and re-sent verbatim on retry:
 *
 *   - `key` is a uuidv7 and never changes for this intent;
 *   - `ifMatch` is the version READ when the intent was formed, so a retry cannot silently
 *     compare-and-swap against a version nobody looked at;
 *   - `action` is the permission code the route enforces, which binds any step-up grant to exactly
 *     this mutation and spends it afterwards;
 *   - `confirm` is the typed confirmation, checked INSIDE the execution path (P13-f H6) rather
 *     than only by disabling a button.
 *
 * A `412`/`409` is NOT retryable with the same intent: the version it names is stale. The screen
 * re-reads and the operator confirms again, which mints a new intent — see `ConflictRemedy`.
 */
export interface ActionIntent {
  readonly key: string;
  readonly path: string;
  readonly method: NonNullable<RequestOptions['method']>;
  /** The permission code the backend checks for this route, when it is step-up gated. */
  readonly action?: string;
  readonly body?: unknown;
  readonly formData?: FormData;
  readonly ifMatch?: number | string;
  /** `If-None-Match: *` — "only if it does not exist". Never sent together with `ifMatch`. */
  readonly ifNoneMatch?: string;
  readonly confirm?: { expected: string; typed: string };
  /** Human-readable description of the exact act, for the audit statement and the retry surface. */
  readonly label?: string;
}

export interface IntentInput extends Omit<ActionIntent, 'key' | 'method'> {
  method?: RequestOptions['method'];
}

/** Mints one intent. Call it ONCE per act the operator asked for, never once per attempt. */
export function newIntent(input: IntentInput): ActionIntent {
  return Object.freeze({
    ...input,
    method: input.method ?? 'POST',
    key: uuidv7(),
  });
}

/**
 * MAY THIS TAB STOP CLAIMING THE STEP-UP GRANT?
 *
 * The SERVER consumes a grant inside the business transaction that performs the act, so the client's
 * record of it may only be dropped on an answer that says that happened — or that the grant is not
 * there to be claimed. This rule used to be "anything but a transport failure", and that was the
 * same misclassification P13-l corrected in the reveal panel, still live for every OTHER guarded
 * mutation on the branch — the release transitions, the takedown, the exception resolution, the
 * identity merge. Three answers are not decisions about the act and must keep the record:
 *
 *   - THE OUTCOME IS UNKNOWN (`ApiError.outcomeUnknown`): a transport failure, or a 5xx that says
 *     nothing about what became of the request. The act may stand on the server, and the only thing
 *     that can settle it is the exact replay under the frozen key — which is precisely what a
 *     dropped record disables, by sending the operator to collect a second factor first.
 *   - THE ACT IS STILL RUNNING (`ApiError.isPendingOutcome`, P13-n): `409 idempotency.in_progress`
 *     is raised while the first attempt's claim is still standing — that request is executing as the
 *     refusal is read, and the same key hands back its outcome once it lands. It is a 409, so the
 *     old rule counted it among the decisions and spent the record; but what the server decided was
 *     about the REPLY, not about the act, and nothing this tab has learned of has consumed anything.
 *     Spending it here sends the operator to raise a second factor for an act the server is in the
 *     middle of performing — and takes away the elevation the collect itself needs.
 *   - THE REFUSAL PROVES NOTHING WAS CONSUMED (`ApiError.isDefinitiveRefusal`): the two typed 503s
 *     the backend raises above every write. `auth.step_up_unavailable` says in as many words that
 *     "a one-action grant cannot be spent and the act is refused rather than performed without one",
 *     and README delta 24.af says a client must not answer it by collecting a factor — which is
 *     exactly what spending the record makes the screens ask for.
 *
 * EVERYTHING THE SERVER DECIDED ABOUT THE ACT STILL SPENDS IT. A 4xx means the server considered the
 * request and answered: either the grant was matched and consumed, or it did not apply to this
 * action. Keeping a badge past that claims an elevation this tab cannot support — and the other 409s
 * (`duty.seat_taken`, `auth.approval_pending`, `idempotency.key_reuse_conflict`) are exactly that
 * kind of answer, which is why only the in-progress one is named above.
 *
 * A screen with a stricter rule of its own — the reveal panel, whose typed refusals each prove the
 * grant intact — mints intents WITHOUT an `action` and owns the decision itself.
 */
function spendsGrantRecord(e: unknown): boolean {
  if (!(e instanceof ApiError)) return true;
  if (e.outcomeUnknown) return false;
  if (e.isPendingOutcome) return false;
  if (e.isDefinitiveRefusal) return false;
  return true;
}

/** The typed-confirmation gate, refused inside the execution path so no caller can skip it. */
export class ConfirmationRequired extends Error {
  readonly expected: string;
  constructor(expected: string) {
    super(`type ${expected} to confirm`);
    this.name = 'ConfirmationRequired';
    this.expected = expected;
  }
}

export type ApiActionResult<T> = UseMutationResult<T, Error, ActionIntent> & {
  /** The exact intent that last failed — the only thing a retry control is allowed to re-send. */
  readonly lastIntent: MutableRefObject<ActionIntent | null>;
  /**
   * Re-sends the exact failed request — key, version AND the caller's own success/error handling.
   *
   * See `useApiAction`: a recovery that re-sent the request but not the state transition was how a
   * replayed 201 arrived at a screen that did nothing with it.
   */
  retryLast: () => void;
  /**
   * The `ETag` the last successful response carried.
   *
   * Some compare-and-swap surfaces have no read route on this branch — the hold policy is written
   * by a `PUT` that returns the new revision and read by nothing — so the tag from the write is the
   * only honest source for the next one.
   */
  readonly lastEtag: MutableRefObject<string | null>;
};

/**
 * Any mutation, driven by intents.
 *
 * Every attempt sends the intent's own `Idempotency-Key`, and every attempt that named an action
 * spends that action's step-up grant on an answer THE SERVER DECIDED — the server consumes the
 * grant inside the business transaction, so a client that kept it would offer a spent grant to the
 * next mutation. An answer that decides nothing keeps it; `spendsGrantRecord` holds the rule and the
 * reasons.
 *
 * AND EVERY ATTEMPT RUNS THE SAME STATE TRANSITION (round-3 #13). React Query's per-call handlers
 * belong to the `mutate` CALL, not to the mutation, so `retryLast` used to replay the request
 * without them. That is precisely the case the frozen key exists for — the first response was lost
 * and the server replays its stored 201 — and the screen that collected it did nothing with the
 * answer: the approvals desk recovered a real approval and rendered no grant token for it, and a
 * recovered withdrawal left the withdrawn approval's token on screen. The options of the originating
 * call are therefore remembered beside the intent and re-applied on every replay of it.
 */
export function useApiAction<T = unknown>(invalidate: (string | null)[] = []): ApiActionResult<T> {
  const client = useQueryClient();
  const lastIntent = useRef<ActionIntent | null>(null);
  const lastEtag = useRef<string | null>(null);
  const lastOptions = useRef<MutateOptions<T, Error, ActionIntent> | undefined>(undefined);

  const mutation = useMutation<T, Error, ActionIntent>({
    mutationFn: async (intent) => {
      lastIntent.current = intent;
      if (intent.confirm && intent.confirm.typed.trim() !== intent.confirm.expected) {
        throw new ConfirmationRequired(intent.confirm.expected);
      }
      const options: RequestOptions = { method: intent.method, idempotencyKey: intent.key };
      if (intent.body !== undefined) options.body = intent.body;
      if (intent.formData) options.formData = intent.formData;
      if (intent.ifMatch !== undefined) options.ifMatch = intent.ifMatch;
      if (intent.ifNoneMatch !== undefined) options.ifNoneMatch = intent.ifNoneMatch;
      try {
        const res = await apiRequest<T>(intent.path, options);
        lastEtag.current = res.etag;
        if (intent.action) spendGrant(intent.action);
        return res.data;
      } catch (e) {
        // The grant is spent for every answer the SERVER DECIDED ABOUT THE ACT — it is consumed
        // inside the business transaction on success, and on a decided refusal it either did not
        // match this action or is gone. It is KEPT for the three answers that decide nothing about
        // it: an outcome this tab cannot name, an act the server says is still running, and a
        // refusal that proves the act never ran. See `spendsGrantRecord`.
        if (intent.action && spendsGrantRecord(e)) {
          spendGrant(intent.action);
        }
        throw e;
      }
    },
    onSuccess: () => {
      for (const key of invalidate) {
        if (key === null) continue;
        void client.invalidateQueries({ queryKey: ['list', key] });
        void client.invalidateQueries({ queryKey: ['resource', key] });
      }
    },
  });

  // Captured BEFORE the assignment below replaces `mutate` on the returned object; `useMutation`
  // hands back a fresh object each render, so this is always React Query's own function.
  const baseMutate = mutation.mutate;

  const mutate = useCallback(
    (intent: ActionIntent, options?: MutateOptions<T, Error, ActionIntent>) => {
      lastOptions.current = options;
      baseMutate(intent, options);
    },
    [baseMutate],
  );

  const retryLast = useCallback(() => {
    const intent = lastIntent.current;
    if (!intent) return;
    // The same key, the same version, and the same thing done with the answer.
    baseMutate(intent, lastOptions.current);
  }, [baseMutate]);

  return Object.assign(mutation, { lastIntent, lastEtag, retryLast, mutate });
}
